import { TwoUIProgressView, TwoUIProgressViewOperationData } from "@amx/ux-twoui-telemetry";
import { Theme, useTheme } from "@amx/ux-twoui-theming";
import { getId, ILabelProps, IRawStyle, IToggleProps, IToggleStyles, Toggle as FluentToggle } from "@fluentui/react";
import React, { forwardRef, RefObject, useEffect, useRef, useState } from "react";
import { timeToShowSpinner } from "../../shared/constants";
import { useComponentLogger } from "../../shared/logging";
import { BooleanInputOnChangeProps, InteractiveElementProps, validateProps } from "../../shared/props";
import { ErrorCallout, ErrorCalloutProps } from "../ErrorCallout/ErrorCallout";
import { Label, LabelState } from "../Label/Label";
import { Spinner } from "../Spinner/Spinner";
import { Stack } from "../Stacks/Stack/Stack";

const mapPositionToInlineBoolean = (position: TogglePosition): FluentToggleLabelPositionProps => {
    if (position === "inline") {
        return { inlineLabel: true };
    }
    return;
};

export type TogglePosition = "inline" | null;

export interface ToggleProps extends InteractiveElementProps {
    /**
     * onChange method specifically for "boolean" inputs,
     * like toggle and checkbox, where something is either
     * on/off
     */
    readonly onChange: BooleanInputOnChangeProps;
    /**
     * Text to display when the toggle is "on"
     */
    readonly onText?: string;
    /**
     * Text to display when the toggle is "off"
     */
    readonly offText?: string;
    /**
     * The state of the toggle, which is handled by consumers
     */
    readonly checked: boolean;
    /**
     * Optional label text for the toggle
     */
    readonly label?: string;
    /**
     * Optional label state for the toggle
     */
    readonly labelState?: LabelState;
    /**
     * Where to position the label
     */
    readonly labelPosition?: TogglePosition;
    /**
     * Operation data of the component
     */
    readonly operationData?: ToggleOperationData;
    /**
     * Optional component ref object to pass for focus
     */
    readonly componentRef?: RefObject<ToggleRef>;
}

type ToggleBaseProps = IToggleProps & Pick<ToggleProps, "labelState">;

export interface ToggleOperationData {
    /**
     * Contain information of the operation dropdown with attach it self to
     */
    readonly progressViewOperationData: TwoUIProgressViewOperationData;

    /**
     * Message when operation failed.
     */
    readonly errorCallout?: ErrorCalloutProps;
}

export interface ToggleRef {
    readonly focus: () => void;
}

type FluentToggleLabelPositionProps = {
    readonly inlineLabel: boolean
} | undefined;

const inlineLabelGap = 16;

interface ToggleLabelProps {
    readonly inlineLabel: ToggleBaseProps["inlineLabel"];
    readonly label: ToggleBaseProps["label"];
    readonly labelState: ToggleBaseProps["labelState"];
    readonly htmlFor?: ILabelProps["htmlFor"];
}

const baseToggleStyles = (theme: Theme, styles?: Partial<IToggleStyles>): Partial<IToggleStyles> => {

    return {
        ...styles,
        text: {
            ...styles?.text as IRawStyle,
            wordBreak: "normal"
        },
        pill: [{ ...styles?.pill as IRawStyle }, {
            selectors: {
                ":focus": {
                    outline: `1px solid ${theme.palette.neutralPrimary}`,
                }
            }
        }]
    };
};

const ToggleLabel: React.FC<ToggleLabelProps> = ({ inlineLabel, label, labelState, htmlFor, children }) => {
    let labelGap = 0;
    let toggleTopPadding = "";

    if (inlineLabel) {
        labelGap = inlineLabelGap;
        toggleTopPadding = "4px 0 0 0";
    }

    return (
        <Stack horizontal={inlineLabel} childrenGap={labelGap}>
            {label && (
                <Stack>
                    <Label labelState={labelState} htmlFor={htmlFor}>
                        {label}
                    </Label>
                </Stack>
            )}
            <Stack horizontal={true} padding={toggleTopPadding} height={32}>
                {children}
            </Stack>
        </Stack>
    );
};

const ToggleBase = forwardRef<HTMLElement, ToggleBaseProps>(({ inlineLabel, label, labelState, children, id, styles, ...baseProps }, ref) => {
    const { current: toggleId } = useRef(id || getId("Toggle"));
    const theme = useTheme();

    return (
        <ToggleLabel inlineLabel={inlineLabel} label={label} labelState={labelState} htmlFor={toggleId}>
            <div ref={ref as React.RefObject<HTMLDivElement>}>
                <FluentToggle {...baseProps} id={toggleId} data-bi-ctrlstate={!!baseProps.checked} styles={baseToggleStyles(theme, styles as Partial<IToggleStyles>)} />
            </div>
            {children}
        </ToggleLabel>
    );
});

const ToggleLoading: React.FC<IToggleProps> = (props: IToggleProps) => {
    const [showSpinner, setShowSpinner] = useState(false);

    const loadingModel: IToggleProps = {
        ...props,
        checked: !props.checked, // this is to set the expected end state. We toggle the value ahead.
        disabled: true,
    };

    useEffect(() => {
        // Do not show Spinner right away, wait for a while before showing
        // spinner. This is designed for better user experience.
        const timeoutID = setTimeout(() => {
            setShowSpinner(true);
        }, timeToShowSpinner);

        return () => clearTimeout(timeoutID);

    }, [/* We only run once to register the time out */]);

    const spinner = (
        <Stack verticalAlign="start" padding="0 0 8px 8px">
            <Spinner aria-label={props.ariaLabel!} />
        </Stack>
    );

    const styles: Partial<IToggleStyles> = {};
    if (showSpinner) {
        styles.text = { display: "none" };
        if (!props.onText && !props.offText) {
            styles.pill = { display: "none" };
        }
    }

    return (
        <ToggleBase {...loadingModel} styles={styles}>
            {showSpinner && spinner}
        </ToggleBase>
    );
};

interface ToggleErrorProps {
    readonly errorCalloutProps: ErrorCalloutProps;
    readonly operationData: ToggleOperationData;
    readonly toggleProps: IToggleProps;
}

const ToggleError: React.FC<ToggleErrorProps> = ({
    errorCalloutProps,
    toggleProps
}) => {
    const toggleRef = useRef<HTMLElement>(null);

    let targetOrTriggerButton: ErrorCalloutProps["targetOrTriggerButton"];
    if (toggleProps.onText || toggleProps.offText) {
        targetOrTriggerButton = errorCalloutProps.targetOrTriggerButton;
    } else {
        targetOrTriggerButton = toggleRef;
    }

    return (
        <ToggleBase {...toggleProps} ref={toggleRef} styles={{ text: { display: "none" } }}>
            <Stack verticalAlign="baseline">
                <ErrorCallout
                    calloutPosition="bottom-center"
                    targetOrTriggerButton={targetOrTriggerButton}
                    errorMessageAriaLabel={errorCalloutProps.errorMessageAriaLabel}
                    errorMessage={errorCalloutProps.errorMessage}
                />
            </Stack>
        </ToggleBase>
    );
};

export const Toggle: React.FC<ToggleProps> = ({
    "aria-label": ariaLabel,
    label,
    labelPosition = null,
    labelState,
    onText,
    offText,
    onChange,
    checked,
    disabled = false,
    operationData,
    componentRef,
    ...otherProps
}: ToggleProps) => {
    useComponentLogger("Toggle", otherProps.id);

    validateProps("Toggle", otherProps);

    const baseProps: ToggleBaseProps = {
        ariaLabel,
        label,
        labelState,
        onText,
        offText,
        onChange,
        checked,
        disabled,
        componentRef,
        ...otherProps,
        ...mapPositionToInlineBoolean(labelPosition)
    };

    if (operationData) {
        let errorView = <ToggleBase {...baseProps} />;

        if (operationData.errorCallout) {
            errorView = (
                <ToggleError toggleProps={baseProps} errorCalloutProps={operationData.errorCallout} operationData={operationData} />
            );
        }

        return (
            <TwoUIProgressView
                operationData={operationData.progressViewOperationData}
                updatingView={<ToggleLoading {...baseProps} />}
                finishedView={<ToggleBase {...baseProps} />}
                updateFailedView={errorView}
            />
        );
    } else {
        return (
            <ToggleBase {...baseProps} />
        );
    }
};
